import { batch } from '@preact/signals-react'
import { CampaignEncoder } from '@susu/headless-commerce/features/configurator/domain/Campaign/CampaignEncoder'
import { Configurator } from '@susu/headless-commerce/features/configurator/domain/Configurator/Configurator'
import type { ConfiguratorDataAccessObject } from '@susu/headless-commerce/features/configurator/domain/Configurator/ConfiguratorDataAccessObject'
import { OptionEncoder } from '@susu/headless-commerce/features/configurator/domain/Option/OptionEncoder'
import { PreloadEncoder } from '@susu/headless-commerce/features/configurator/domain/Preload/PreloadEncoder'
import { PreviewEncoder } from '@susu/headless-commerce/features/configurator/domain/Preview/PreviewEncoder'
import { SelectionEncoder } from '@susu/headless-commerce/features/configurator/domain/Selection/SelectionEncoder'
import { StepEncoder } from '@susu/headless-commerce/features/configurator/domain/Step/StepEncoder'
import { isNotUndefined, isUndefined } from '@susu/undefined'

export class ConfiguratorEncoder {
  static encode(instance: Configurator): ConfiguratorDataAccessObject {
    return {
      campaigns: instance.campaigns.value.map(CampaignEncoder.encode),
      country: instance.country.value,
      locale: instance.locale.value,
      currentStep: isNotUndefined(instance.currentStep.value)
        ? StepEncoder.encode(instance.currentStep.value)
        : undefined,
      currentStepIndex: instance.currentStepIndex.value,
      modelId: instance.modelId.value,
      orderedOptions: instance.orderedOptions.value.map(OptionEncoder.encode),
      pageType: instance.pageType.value,
      preload: isNotUndefined(instance.preload.value)
        ? PreloadEncoder.encode(instance.preload.value)
        : undefined,
      preview: PreviewEncoder.encode(instance.preview.value),
      previousStepName: instance.previousStepName.value,
      defaultCampaign: instance.defaultCampaign.value
        ? CampaignEncoder.encode(instance.defaultCampaign.value)
        : undefined,
      selectedCampaign: instance.selectedCampaign.value
        ? CampaignEncoder.encode(instance.selectedCampaign.value)
        : undefined,
      selectedOption: isNotUndefined(instance.selectedOption.value)
        ? OptionEncoder.encode(instance.selectedOption.value)
        : undefined,
      searchParams: instance.searchParams.value,
      selection: SelectionEncoder.encode(instance.selection.value),
      steps: instance.allSteps.map(StepEncoder.encode),
    }
  }

  static decode(properties: ConfiguratorDataAccessObject) {
    const instance = new Configurator(
      properties.country,
      properties.locale,
      properties.searchParams,
      properties.campaigns.map(CampaignEncoder.decode),
      properties.modelId,
      properties.pageType,
      undefined,
      [],
    )

    const steps = properties.steps.map((a) => StepEncoder.decode(instance, a))

    batch(() => {
      instance.steps = steps
      instance.country.value = properties.country
      instance.currentStep.value = properties.currentStep
        ? StepEncoder.decode(instance, properties.currentStep)
        : undefined
      instance.currentStepIndex.value = properties.currentStepIndex
      instance.modelId.value = properties.modelId

      const currentStep = instance.currentStep.value
      if (isUndefined(currentStep)) {
        instance.orderedOptions.value = []
      } else {
        instance.orderedOptions.value = properties.orderedOptions.map((a) =>
          OptionEncoder.decode(instance, currentStep, a),
        )
      }

      instance.pageType.value = properties.pageType
      if (isNotUndefined(properties.preload)) {
        instance.preload.value = PreloadEncoder.decode(properties.preload)
      }
      instance.preview.value = PreviewEncoder.decode(properties.preview)
      instance.previousStepName.value = properties.previousStepName
      instance.defaultCampaign.value = properties.defaultCampaign
        ? CampaignEncoder.decode(properties.defaultCampaign)
        : undefined
      instance.selectedCampaign.value = properties.selectedCampaign
        ? CampaignEncoder.decode(properties.selectedCampaign)
        : undefined
      if (
        isNotUndefined(currentStep) &&
        isNotUndefined(properties.selectedOption)
      ) {
        instance.selectedOption.value = OptionEncoder.decode(
          instance,
          currentStep,
          properties.selectedOption,
        )
      }

      // Selection
      instance.selection.value.current.value = properties.selection.current
      instance.selection.value.default.value = properties.selection.default
      instance.selection.value.previous.value = properties.selection.previous
      instance.selection.value.recommended.value =
        properties.selection.recommended
    })

    return instance
  }
}
